import { globalAppVersion } from 'globalConstants';
import UAParser from 'ua-parser-js';
import AppError from 'utils/appError';
import { safeStringify } from 'utils/stringify';

class EnvInfo {
  appVersion: string;

  appEnv: string;

  screenHeight?: number;

  screenWidth?: number;

  envInfo?: UAParser.IResult;

  constructor() {
    this.appVersion = '';
    this.appEnv = '';
  }
}

class Config {
  static appURL: string | undefined;

  getVersion(): string {
    return globalAppVersion;
  }

  // the REACT_APP_STAGE environment variable is injected into the build in package.json in the scripts section

  isLocal(): boolean {
    return process.env.REACT_APP_STAGE?.trimEnd() === 'local'; // local front-end and local API
  }

  isDev(): boolean {
    return process.env.REACT_APP_STAGE?.trimEnd() === 'development'; // production build in development mode in Azure
  }

  isTest(): boolean {
    return process.env.REACT_APP_STAGE?.trimEnd() === 'test'; // run testing
  }

  isProd(): boolean {
    return process.env.REACT_APP_STAGE?.trimEnd() === 'production'; // production build in production mode in Azure
  }

  isNorth(): boolean {
    return process.env.REACT_APP_STAGE?.trimEnd() === 'north'; // production build in north zone mode in Azure
  }

  isFront(): boolean {
    return process.env.REACT_APP_STAGE?.trimEnd() === 'front'; // local front-end and development back-end in Azure
  }

  getAppEnv(): string {
    return process.env.REACT_APP_STAGE ? process.env.REACT_APP_STAGE?.trimEnd() : 'unknown';
  }

  // Support widget provider: defaults to HubSpot unless explicitly set to 'freshdesk'
  isHubSpotSupportEnabled(): boolean {
    return process.env.REACT_APP_SUPPORT_PROVIDER?.trimEnd() !== 'freshdesk';
  }

  getAppURL(): string {
    if (Config.appURL === undefined) {
      Config.appURL = document.location.protocol + '//' + document.location.host;
      if (Config.appURL.endsWith('/')) {
        Config.appURL = Config.appURL.slice(0, -1);
      }
      console.log(`App URL set: ${Config.appURL}`);
    }

    return Config.appURL;
  }

  getImageURL(): string {
    return this.getAppURL() + '/images';
  }

  get(name: string): string {
    if (this.isProd()) {
      return this.getProd(name);
    }

    if (this.isTest()) {
      return this.getTest(name);
    }

    if (this.isDev() || this.isFront()) {
      return this.getTest(name); //dev experiences a CORS issue
    }

    if (this.isLocal()) {
      return this.getLocal(name);
    }

    if (this.isNorth()) {
      return this.getNorth(name);
    }

    throw new AppError(`Unknown environment: ${this.getAppEnv()}`);
  }

  getLocal(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'debug';
      case 'App.BaseURL':
        return 'http://localhost:3000';
      case 'App.ClientId':
        return 'd67b2608-bb92-4605-9c74-52fdcda660e5';
      case 'Api.BaseURL':
        return 'http://localhost:61308';
      case 'Copilot.BaseURL':
        return 'http://localhost:65317';
      case 'Api.ClientId':
        return 'c03d205e-3a66-4a42-a9d6-cc9319b44858';
      case 'Svc.ClientId':
        return '2d32c615-74e9-4f4c-98ef-b493a2f62c42';
      case 'Teams.Task.ClientId':
        return '13ce7ef9-6cd9-4fd8-8fa3-aa70939a38d7';
      case 'Teams.Library.ClientId':
        return '5da6ec53-4cf8-47f8-8669-04d9c12f2b71';
      case 'EntraID.ClientId':
        return 'd09bd010-cfe3-4f7c-aa8e-6003a671c9d4';
      case 'Planner.ClientId':
        return 'c7122fbc-64fb-416a-9027-6eef41cf4bde';
      case 'NIS2.PackagePriceId':
        return 'price_1QwRewB6wRFY316YS1Zobq15';
      case 'SecureScoreM365.ClientId':
        return '071830fc-8167-4a48-94cd-a90fd3db77e3';
      case 'SecureScoreIdentity.ClientId':
        return 'f4964879-561c-4ae5-b43a-bf47b3db3fe2';
      case 'SecureScoreCloud.ClientId':
        return '93815a41-173d-4e40-9e8b-d594ad1f291d';
      case 'SecureScoreDevice.ClientId':
        return '2049c928-49be-4e69-8847-e407a243a5a6';
      case 'EntraMFA.ClientId':
        return 'e172cf4a-13c6-41f5-a762-2de778880ba0';
      case 'EntraPolicy.ClientId':
        return 'a973d080-944e-45a1-94f6-cbfa8701353f';
      case 'AzureResources.ClientId':
        //set after the app registration ISOPlanner Azure reporting (phase 0)
        return '';
      case 'IdentityProviders.Google.ClientId':
        //the Google Workspace sign-in is off until the stage has an OAuth client
        return '261876667167-4ladp63fhq7m8qhdktof46ep2j7ov5b5.apps.googleusercontent.com';
      case 'IdentityProviders.Oidc.Enabled':
        //organization sign-in through an allow-listed OpenID Connect issuer is off until a stage enables it
        return 'true';
      default:
        throw new AppError(`Value of dev appsetting ${name} not defined.`);
    }
  }

  getDev(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'debug';
      case 'App.BaseURL':
        return 'https://isoplanner-dev.azurewebsites.net';
      case 'App.ClientId':
        return 'd67b2608-bb92-4605-9c74-52fdcda660e5';
      case 'Api.BaseURL':
        return 'https://isoplanner-dev-api.azurewebsites.net';
      case 'Copilot.BaseURL':
        return 'https://isoplanner-dev-copilot.azurewebsites.net';
      case 'Api.ClientId':
        return 'c03d205e-3a66-4a42-a9d6-cc9319b44858';
      case 'Svc.ClientId':
        return '2d32c615-74e9-4f4c-98ef-b493a2f62c42';
      case 'Teams.Task.ClientId':
        return 'db58ae59-a740-4601-aa33-4d2ac87e95d3';
      case 'Teams.Library.ClientId':
        return '5da6ec53-4cf8-47f8-8669-04d9c12f2b71';
      case 'EntraID.ClientId':
        return 'd09bd010-cfe3-4f7c-aa8e-6003a671c9d4';
      case 'Planner.ClientId':
        return 'c7122fbc-64fb-416a-9027-6eef41cf4bde';
      case 'NIS2.PackagePriceId':
        return 'price_1QwRewB6wRFY316YS1Zobq15';
      case 'SecureScoreM365.ClientId':
        return '071830fc-8167-4a48-94cd-a90fd3db77e3';
      case 'SecureScoreIdentity.ClientId':
        return 'f4964879-561c-4ae5-b43a-bf47b3db3fe2';
      case 'SecureScoreCloud.ClientId':
        return '93815a41-173d-4e40-9e8b-d594ad1f291d';
      case 'SecureScoreDevice.ClientId':
        return '2049c928-49be-4e69-8847-e407a243a5a6';
      case 'EntraMFA.ClientId':
        return 'e172cf4a-13c6-41f5-a762-2de778880ba0';
      case 'EntraPolicy.ClientId':
        return 'a973d080-944e-45a1-94f6-cbfa8701353f';
      case 'AzureResources.ClientId':
        //set after the app registration ISOPlanner Azure reporting (phase 0)
        return '';
      case 'IdentityProviders.Google.ClientId':
        //the Google Workspace sign-in is off until the stage has an OAuth client
        return '';
      case 'IdentityProviders.Oidc.Enabled':
        //organization sign-in through an allow-listed OpenID Connect issuer is off until a stage enables it
        return 'false';
      default:
        throw new AppError(`Value of dev appsetting ${name} not defined.`);
    }
  }

  getTest(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'debug';
      case 'App.BaseURL':
        return 'https://test.isoplanner.app';
      case 'App.ClientId':
        return 'd819e0c9-4591-4444-a397-584e9c029e9e';
      case 'Api.BaseURL':
        return 'https://test-api.isoplanner.app';
      case 'Copilot.BaseURL':
        return 'https://test-copilot.isoplanner.app/';
      case 'Api.ClientId':
        return '60c2ef0e-b6cc-4d49-8ada-bacc7a6fd277';
      case 'Svc.ClientId':
        return '9f1673e2-07b2-47b2-915f-15f66b93bbcb';
      case 'Teams.Task.ClientId':
        return 'b61c8ab3-0174-4613-a40e-f4bf8961a77f';
      case 'Teams.Library.ClientId':
        return '756b73dc-d065-4ae7-b5c3-aff8539dd422';
      case 'EntraID.ClientId':
        return 'ab16023d-76f3-4c2e-ad4c-5db5f1dcf614';
      case 'Planner.ClientId':
        return '226433ec-c564-432e-a117-794859edfec3';
      case 'NIS2.PackagePriceId':
        return 'price_1QwRewB6wRFY316YS1Zobq15';
      case 'SecureScoreM365.ClientId':
        return '50515554-f7b3-4580-a6b0-f80053defad4';
      case 'SecureScoreIdentity.ClientId':
        return '2c7c173d-7409-45a8-b0cc-6fcb80c33530';
      case 'SecureScoreCloud.ClientId':
        return 'a6e0e70c-010d-4cfe-9752-de10a4da5c8a';
      case 'SecureScoreDevice.ClientId':
        return '6e39a235-d6c0-4ffd-9f82-2ffc72c388e5';
      case 'EntraMFA.ClientId':
        return '9a085a9d-ea79-4669-8889-285d814c738b';
      case 'EntraPolicy.ClientId':
        return '45d3e4c7-5a36-414a-94a9-ef537a78bd8c';
      case 'AzureResources.ClientId':
        //set after the app registration ISOPlanner Azure reporting (phase 0)
        return '';
      case 'IdentityProviders.Google.ClientId':
        return '';
      case 'IdentityProviders.Oidc.Enabled':
        return 'false'; //organization sign-in through an allow-listed OpenID Connect issuer is off until a stage enables it
      default:
        throw new AppError(`Value of test appsetting ${name} not defined.`);
    }
  }

  getProd(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'error';
      case 'App.BaseURL':
        return 'https://portal.isoplanner.app';
      case 'App.ClientId':
        return '2a2fc0d2-c9ab-4e14-9d0b-79618085e85e';
      case 'Api.BaseURL':
        return 'https://api.isoplanner.app/';
      case 'Copilot.BaseURL':
        return 'https://copilot.isoplanner.app/';
      case 'Api.ClientId':
        return 'ea7e8086-f302-4147-aae3-779149fb15aa';
      case 'Svc.ClientId':
        return '9a056f37-32fc-48b0-8075-e032dec8e3ac';
      case 'Teams.Task.ClientId':
        return '07af2df9-11f4-4e3c-bf62-056f70cfdbc0';
      case 'Teams.Library.ClientId':
        return '3c02ce55-89a4-48f9-9462-3de895b23d2d';
      case 'EntraID.ClientId':
        return '35553ac5-1d35-49af-be17-be15c2e0a931';
      case 'Planner.ClientId':
        return '0431033e-88e6-49d7-b8d0-bf083b4b5456';
      case 'NIS2.PackagePriceId':
        return 'price_1QuEsZB6wRFY316Y0N2uzuvE';
      case 'SecureScoreM365.ClientId':
        return '076d0c77-4939-43db-932c-f3be01577654';
      case 'SecureScoreIdentity.ClientId':
        return '1b151ded-2e48-46d0-a64c-ca2dc3442ebc';
      case 'SecureScoreCloud.ClientId':
        return 'f7d7a551-029f-4577-a3df-51f3010c5034';
      case 'SecureScoreDevice.ClientId':
        return '5e3ddaa6-286e-4d4f-b193-5c6ddb55f405';
      case 'EntraMFA.ClientId':
        return '2727fc6a-3ff4-4de6-b768-a8fd134b4a9b';
      case 'EntraPolicy.ClientId':
        return 'e2dbc6d8-b290-40a5-83c2-dcf8b692dea6';
      case 'AzureResources.ClientId':
        //set after the app registration ISOPlanner Azure reporting (phase 0)
        return '';
      case 'IdentityProviders.Google.ClientId':
        return '';
      case 'IdentityProviders.Oidc.Enabled':
        return 'false'; //organization sign-in through an allow-listed OpenID Connect issuer is off until a stage enables it
      default:
        throw new AppError(`Value of prod appsetting ${name} not defined.`);
    }
  }

  getNorth(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'error';
      case 'App.BaseURL':
        return 'https://portal2.isoplanner.app';
      case 'App.ClientId':
        return '2a2fc0d2-c9ab-4e14-9d0b-79618085e85e';
      case 'Api.BaseURL':
        return 'https://api2.isoplanner.app/';
      case 'Api.ClientId':
        return 'ea7e8086-f302-4147-aae3-779149fb15aa';
      case 'Svc.ClientId':
        return '9a056f37-32fc-48b0-8075-e032dec8e3ac';
      case 'Teams.Task.ClientId':
        return '07af2df9-11f4-4e3c-bf62-056f70cfdbc0';
      case 'Teams.Library.ClientId':
        return '3c02ce55-89a4-48f9-9462-3de895b23d2d';
      case 'EntraID.ClientId':
        return '35553ac5-1d35-49af-be17-be15c2e0a931';
      case 'Planner.ClientId':
        return '0431033e-88e6-49d7-b8d0-bf083b4b5456';
      case 'NIS2.PackagePriceId':
        return 'price_1QuEsZB6wRFY316Y0N2uzuvE';
      case 'SecureScoreM365.ClientId':
        return '076d0c77-4939-43db-932c-f3be01577654';
      case 'SecureScoreIdentity.ClientId':
        return '1b151ded-2e48-46d0-a64c-ca2dc3442ebc';
      case 'SecureScoreCloud.ClientId':
        return 'f7d7a551-029f-4577-a3df-51f3010c5034';
      case 'SecureScoreDevice.ClientId':
        return '5e3ddaa6-286e-4d4f-b193-5c6ddb55f405';
      case 'EntraMFA.ClientId':
        return '2727fc6a-3ff4-4de6-b768-a8fd134b4a9b';
      case 'EntraPolicy.ClientId':
        return 'e2dbc6d8-b290-40a5-83c2-dcf8b692dea6';
      case 'AzureResources.ClientId':
        //set after the app registration ISOPlanner Azure reporting (phase 0)
        return '';
      case 'IdentityProviders.Google.ClientId':
        return '';
      case 'IdentityProviders.Oidc.Enabled':
        return 'false'; //organization sign-in through an allow-listed OpenID Connect issuer is off until a stage enables it
      default:
        throw new AppError(`Value of prod appsetting ${name} not defined.`);
    }
  }

  getEnvInfo(): string {
    try {
      const envInfo = new EnvInfo();
      envInfo.appVersion = this.getVersion();
      envInfo.appEnv = this.getAppEnv();
      envInfo.screenHeight = window.screen.height;
      envInfo.screenWidth = window.screen.width;

      const parser = new UAParser();
      envInfo.envInfo = parser.getResult();

      return JSON.stringify(envInfo);
    } catch (err) {
      return safeStringify(err);
    }
  }
}

const config = new Config();
export default config;
